import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * How a surface fills in the parts of the chrome that belong to it.
 *
 * The header's identity pill, the breadcrumb and the validation line all sit
 * *outside* the surface's DOM but describe what is *inside* it — the explorer
 * knows which method is open, the shell does not. Two ways to resolve that: let
 * every surface render its own header (five headers that drift apart), or give
 * the shell a slot the surface fills. This is the slot.
 *
 * A surface declares its chrome once, with `useSurfaceChrome`, and the shell
 * clears it on unmount so a stale breadcrumb never survives a navigation. That
 * automatic reset is the reason this is a hook and not a store the surface
 * writes to directly.
 */

export type SurfaceStatusTone = 'success' | 'warning' | 'error' | 'neutral';

export interface SurfaceStatus {
  readonly tone: SurfaceStatusTone;
  /**
   * The whole line, e.g. "Graph validated 26 Aug 2026 09:14 · 0 errors ·
   * 2 advisories". Text, not a count: the dot is the fast channel and the words
   * are the channel that survives forced colours.
   */
  readonly label: ReactNode;
}

export interface SurfaceIdentity {
  /** Canonical ontology id, shown monospaced: `dpem:DPEM · v2.7`. */
  readonly id: string;
  /** Lifecycle state, e.g. `PUBLISHED`. Rendered emphasised. */
  readonly state?: string;
  readonly nodeCount?: number;
  /** Tints the pill's rule. The variant is also named in the pill's text. */
  readonly variant?: 'standard' | 'agentic';
}

export interface ShellBreadcrumbItem {
  readonly label: ReactNode;
  /** Omit on the last item — the page you are on is not a link to itself. */
  readonly to?: string;
}

export interface SurfaceChrome {
  readonly breadcrumb: readonly ShellBreadcrumbItem[];
  readonly status: SurfaceStatus | null;
  readonly identity: SurfaceIdentity | null;
  /** Controls docked at the right of the subhead — a variant switch, an export. */
  readonly actions: ReactNode;
}

const EMPTY_CHROME: SurfaceChrome = Object.freeze({
  breadcrumb: [],
  status: null,
  identity: null,
  actions: null,
});

interface SurfaceChromeApi {
  readonly chrome: SurfaceChrome;
  readonly publish: (chrome: Partial<SurfaceChrome>) => void;
  readonly clear: () => void;
}

const SurfaceChromeContext = createContext<SurfaceChromeApi | null>(null);

export function SurfaceChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<SurfaceChrome>(EMPTY_CHROME);

  const publish = useCallback((next: Partial<SurfaceChrome>) => {
    setChrome({ ...EMPTY_CHROME, ...next });
  }, []);

  const clear = useCallback(() => setChrome(EMPTY_CHROME), []);

  const api = useMemo<SurfaceChromeApi>(() => ({ chrome, publish, clear }), [chrome, publish, clear]);

  return <SurfaceChromeContext.Provider value={api}>{children}</SurfaceChromeContext.Provider>;
}

/** Read the current chrome. Used by the shell; surfaces use the setter below. */
export const useShellChrome = (): SurfaceChrome => {
  const api = useContext(SurfaceChromeContext);
  return api?.chrome ?? EMPTY_CHROME;
};

/**
 * Declare this surface's chrome.
 *
 * `chrome` must be referentially stable — wrap it in `useMemo`. It is a
 * dependency of the effect that publishes it, and an object rebuilt every render
 * would publish on every render. That requirement is on the caller rather than
 * hidden behind a deep comparison because `actions` and `label` are ReactNodes,
 * which cannot be compared meaningfully.
 *
 * Outside a provider this is a no-op, so a surface can be rendered in isolation
 * in a test without standing up the shell.
 */
export const useSurfaceChrome = (chrome: Partial<SurfaceChrome>): void => {
  const api = useContext(SurfaceChromeContext);
  const publish = api?.publish;
  const clear = api?.clear;

  useEffect(() => {
    if (publish === undefined || clear === undefined) return;
    publish(chrome);
    return clear;
  }, [chrome, publish, clear]);
};
